import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';
import ShareSocialButton from '../components/common/ShareSocialButton';

const Notifications = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [generatingImage, setGeneratingImage] = useState(null); // ID del logro que se está generando

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('Cargando notificaciones del usuario...');

        // Obtener notificaciones reales desde el backend
        const response = await API.get('/users/notifications?page=1&limit=50');

        if (response.data.success) {
          const notificacionesReales = response.data.data.notifications.map(notif => ({
            id: notif.id,
            type: notif.type,
            title: notif.title,
            message: notif.message,
            date: new Date(notif.date),
            read: notif.read,
            link: notif.link,
            priority: notif.priority,
            icon: notif.icon,
            color: notif.color,
            metadata: notif.data || {} // El backend devuelve 'data', no 'metadata'
          }));

          setNotifications(notificacionesReales);
          console.log(`Cargadas ${notificacionesReales.length} notificaciones`);
          console.log('Notificaciones con metadata:', notificacionesReales.filter(n => n.type === 'achievement').map(n => ({
            title: n.title,
            metadata: n.metadata,
            hasLogros: !!n.metadata?.logros,
            logrosCount: n.metadata?.logros?.length || 0
          })));
        } else {
          throw new Error(response.data.message || 'Error al obtener notificaciones');
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setError('Error al cargar las notificaciones');

        // En caso de error, mostrar array vacío en lugar de datos mock
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      console.log('🔄 Marcando notificación como leída:', id);
      console.log('📋 Notificaciones antes del cambio:', notifications.map(n => ({ id: n.id, read: n.read })));

      // Hacer la petición al backend
      await API.put(`/users/notifications/${id}/read`);

      // Actualizar el estado local independientemente de la respuesta
      // porque sabemos que la operación funciona en el backend
      const updatedNotifications = notifications.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification,
      );

      console.log('📋 Notificaciones después del cambio:', updatedNotifications.map(n => ({ id: n.id, read: n.read })));

      setNotifications(updatedNotifications);

      console.log('✅ Notificación marcada como leída exitosamente');

      // Limpiar cualquier error previo
      setError(null);

    } catch (error) {
      console.error('❌ Error al marcar notificación como leída:', error);

      // Actualizar el estado local de todas formas porque sabemos que funciona
      const updatedNotifications = notifications.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification,
      );
      setNotifications(updatedNotifications);

      console.log('🔄 Estado actualizado a pesar del error de respuesta');

      // Solo mostrar error si realmente falló la operación
      // (por ejemplo, problemas de red)
      if (error.response && error.response.status >= 500) {
        setError('Error de servidor al marcar la notificación como leída');
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      console.log('Marcando todas las notificaciones como leídas...');

      const response = await API.put('/users/notifications/mark-all-read');

      if (response.data.success) {
        // Actualizar el estado local
        setNotifications(notifications.map((notification) => ({ ...notification, read: true })));
        console.log(`${response.data.data.updatedCount} notificaciones marcadas como leídas`);
      } else {
        throw new Error(response.data.message || 'Error al marcar todas como leídas');
      }
    } catch (error) {
      console.error('Error al marcar todas las notificaciones como leídas:', error);
      setError('Error al marcar todas las notificaciones como leídas');
    }
  };

  const handleDeleteNotification = (id) => {
    // TODO: Implementar endpoint para eliminar notificaciones
    console.log('Eliminar notificación no implementado aún:', id);

    // Mostrar mensaje informativo en lugar de error
    // Crear un mensaje temporal que desaparezca después de unos segundos
    const tempMessage = 'La función de eliminar notificaciones estará disponible próximamente';

    // Mostrar el mensaje temporalmente
    setError(tempMessage);

    // Limpiar el mensaje después de 3 segundos
    setTimeout(() => {
      setError(null);
    }, 3000);
  };

  const handleClearAll = () => {
    // TODO: Implementar endpoint para eliminar todas las notificaciones
    console.log('Eliminar todas las notificaciones no implementado aún');

    // Mostrar mensaje informativo temporal
    const tempMessage = 'La función de eliminar todas las notificaciones estará disponible próximamente';
    setError(tempMessage);

    // Limpiar el mensaje después de 3 segundos
    setTimeout(() => {
      setError(null);
    }, 3000);
  };

  const toggleExpandNotification = (notificationId) => {
    setExpandedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Función para generar imagen con Canvas para Instagram
  const generateImageForInstagram = async (logroData, futbolistaData, partidoData) => {
    const logroKey = `${partidoData.partidoId}_${futbolistaData.futbolistaId}_${logroData.nombre}`;
    setGeneratingImage(logroKey);

    try {
      const logroImageUrl = getLogroImageUrl(logroData.nombre, futbolistaData.genero);
      const logroDisplayName = getLogroDisplayName(logroData.nombre);

      // Crear canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Dimensiones (formato Instagram post 1080x1350)
      const width = 1080;
      const height = 1350;
      canvas.width = width;
      canvas.height = height;

      // Fondo blanco
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);

      // Marco exterior decorativo
      const borderWidth = 30;
      const borderRadius = 40;

      // Gradiente para el marco
      const borderGradient = ctx.createLinearGradient(0, 0, width, height);
      borderGradient.addColorStop(0, '#667eea');
      borderGradient.addColorStop(1, '#764ba2');

      // Dibujar marco con bordes redondeados
      ctx.strokeStyle = borderGradient;
      ctx.lineWidth = borderWidth;
      ctx.beginPath();
      ctx.roundRect(borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth, borderRadius);
      ctx.stroke();

      // Cargar imagen del logro
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = logroImageUrl;
      });

      // Dibujar imagen del logro centrada - más alargada (proporción de carta)
      // Las cartas suelen tener proporción 2:3 (ancho:alto)
      const imgWidth = 500;  // Más estrecha
      const imgHeight = 750; // Más alta
      const imgX = (width - imgWidth) / 2;
      const imgY = 150;

      // Sombra para la carta
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 10;

      // Dibujar imagen del logro con proporción de carta
      ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight);

      // Resetear sombra
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Header - Logo y título (usando Roboto)
      ctx.fillStyle = '#667eea';
      ctx.font = 'bold 48px Roboto, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🏆 Liga ProKids', width / 2, 80);

      // Información del futbolista (usando Roboto)
      const infoY = imgY + imgHeight + 80;

      ctx.fillStyle = '#212529';
      ctx.font = 'bold 56px Roboto, Arial, sans-serif';
      ctx.fillText(futbolistaData.nombre, width / 2, infoY);

      ctx.fillStyle = '#667eea';
      ctx.font = 'bold 42px Roboto, Arial, sans-serif';
      ctx.fillText(logroDisplayName, width / 2, infoY + 60);

      // ProPoints
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 64px Roboto, Arial, sans-serif';
      ctx.fillText(`+${logroData.puntos} ProPoints`, width / 2, infoY + 130);

      // Información del partido
      ctx.fillStyle = '#6c757d';
      ctx.font = '36px Roboto, Arial, sans-serif';
      ctx.fillText(`Jornada ${partidoData.jornada}`, width / 2, infoY + 190);

      ctx.font = '32px Roboto, Arial, sans-serif';
      const partidoInfo = `${partidoData.sede || 'Sede'} - ${partidoData.torneo || 'Torneo'}`;
      ctx.fillText(partidoInfo, width / 2, infoY + 235);

      if (partidoData.fecha) {
        const fecha = new Date(partidoData.fecha).toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        ctx.fillText(fecha, width / 2, infoY + 275);
      }

      // Convertir canvas a blob y descargar
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${futbolistaData.nombre.replace(/\s+/g, '_')}_${logroDisplayName.replace(/\s+/g, '_')}_ProKids.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setGeneratingImage(null);
      }, 'image/png');

    } catch (error) {
      console.error('Error generando imagen:', error);
      alert('Error al generar la imagen. Por favor, intenta de nuevo.');
      setGeneratingImage(null);
    }
  };

  // Helper: Normalizar nombre de logro de BD a identificador técnico URL-friendly
  const normalizeLogroNombre = (logroNombre) => {
    // Simplemente convertir a minúsculas y reemplazar espacios por guiones bajos
    // Esto mantiene la consistencia con los nombres de la BD
    return logroNombre
      .toLowerCase()
      .normalize('NFD') // Normalizar caracteres Unicode
      .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
      .replace(/\s+/g, '_'); // Reemplazar espacios por guiones bajos
  };

  const getLogroImageUrl = (logroNombre, genero) => {
    const isFemenino = genero === 'F';

    // Normalizar el nombre del logro primero
    const normalizedNombre = normalizeLogroNombre(logroNombre);

    const imageMapMasculino = {
      'participacion': '/images/jugadorcampo-1x.jpeg',
      'victoria': '/images/jugadorcampo-1x.jpeg',
      'destacado': '/images/jugadorcampo-1x.jpeg',
      'el_jugon': '/images/eljugon-1x.jpeg',
      'el_cerrojo': '/images/cerrojo-1x.jpeg',
      'el_desequilibrante': '/images/desequilibrante-1x.jpeg',
      'el_lider': '/images/lider-1x.jpeg',
      'el_mejor_companero': '/images/mejorcompanero-1x.jpeg',
      'mayor_progresion': '/images/progresion-1x.jpeg',
      'constancia': '/images/constante-1x.jpeg',
      'ganador_incansable': '/images/incansable-1x.jpeg',
      'portero': '/images/portero-1x.jpeg'
    };

    const imageMapFemenino = {
      'participacion': '/images/la_jugadorcampo.png',
      'victoria': '/images/la_jugadorcampo.png',
      'destacado': '/images/la_jugadorcampo.png',
      'el_jugon': '/images/la_eljugon.png',
      'el_cerrojo': '/images/la_cerrojo.png',
      'el_desequilibrante': '/images/la_desequilibrante.png',
      'el_lider': '/images/la_lider.png',
      'el_mejor_companero': '/images/la_mejorcompanero.png',
      'mayor_progresion': '/images/la_progresion.png',
      'constancia': '/images/la_constante.png',
      'ganador_incansable': '/images/la_incansable.png',
      'portero': '/images/la_portero.png'
    };

    const imageMap = isFemenino ? imageMapFemenino : imageMapMasculino;
    const defaultImage = isFemenino ? '/images/la_jugadorcampo.png' : '/images/jugadorcampo-1x.jpeg';

    return imageMap[normalizedNombre] || defaultImage;
  };

  const getLogroDisplayName = (logroNombre) => {
    // Normalizar primero
    const normalized = normalizeLogroNombre(logroNombre);

    const displayNames = {
      'participacion': 'Participación',
      'victoria': 'Victoria',
      'destacado': 'Destacado',
      'el_jugon': 'El Jugón',
      'el_cerrojo': 'El Cerrojo',
      'el_desequilibrante': 'El Desequilibrante',
      'el_lider': 'El Líder',
      'el_mejor_companero': 'El Mejor Compañero',
      'mayor_progresion': 'Mayor Progresión',
      'constancia': 'Constancia',
      'ganador_incansable': 'Ganador Incansable',
      'portero': 'Portero'
    };
    return displayNames[normalized] || logroNombre;
  };

  const getFilteredNotifications = () => {
    switch (activeTab) {
      case 'unread':
        return notifications.filter((notification) => !notification.read);
      case 'registration':
        return notifications.filter((notification) => notification.type === 'registration');
      case 'match':
        return notifications.filter((notification) => notification.type === 'match');
      case 'achievement':
        return notifications.filter((notification) => notification.type === 'achievement');
      case 'payment':
        return notifications.filter((notification) => notification.type === 'payment');
      case 'system':
        return notifications.filter((notification) => notification.type === 'system');
      default:
        return notifications;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'registration':
        return 'person_add';
      case 'match':
        return 'sports_soccer';
      case 'achievement':
        return 'emoji_events';
      case 'payment':
        return 'payment';
      case 'system':
        return 'info';
      case 'announcement':
        return 'campaign';
      case 'reminder':
        return 'schedule';
      default:
        return 'notifications';
    }
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications.filter((notification) => !notification.read).length;

  if (loading) {
    return <div className="loading">Cargando notificaciones...</div>;
  }

  if (error) {
    // Determinar si es un mensaje informativo o un error real
    const isInfoMessage = error.includes('estará disponible próximamente');
    const messageClass = isInfoMessage ? 'info-message' : 'error';

    return (
      <div className={messageClass}>
        {isInfoMessage && <span className="info-icon">ℹ️</span>}
        {error}
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="container">
        <div className="page-header">
          <h1>Notificaciones</h1>
          <div className="header-actions">
            {unreadCount > 0 && (
              <button className="btn btn-secondary" onClick={handleMarkAllAsRead}>
                Marcar todas como leídas
              </button>
            )}
            {notifications.length > 0 && (
              <button className="btn btn-danger" onClick={handleClearAll}>
                Borrar todas
              </button>
            )}
          </div>
        </div>

        <div className="notifications-content">
          <div className="notifications-tabs">
            <button
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              Todas
              <span className="badge">{notifications.length}</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              No leídas
              <span className="badge">{unreadCount}</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'registration' ? 'active' : ''}`}
              onClick={() => setActiveTab('registration')}
            >
              Inscripciones
            </button>
            <button
              className={`tab-button ${activeTab === 'match' ? 'active' : ''}`}
              onClick={() => setActiveTab('match')}
            >
              Partidos
            </button>
            <button
              className={`tab-button ${activeTab === 'achievement' ? 'active' : ''}`}
              onClick={() => setActiveTab('achievement')}
            >
              Logros
            </button>
            <button
              className={`tab-button ${activeTab === 'payment' ? 'active' : ''}`}
              onClick={() => setActiveTab('payment')}
            >
              Pagos
            </button>
            <button
              className={`tab-button ${activeTab === 'system' ? 'active' : ''}`}
              onClick={() => setActiveTab('system')}
            >
              Sistema
            </button>
          </div>

          <div className="notifications-list">
            {filteredNotifications.length === 0 ? (
              <div className="no-notifications">
                <span className="material-icons">notifications_off</span>
                <p>No tienes notificaciones</p>
              </div>
            ) : (
              filteredNotifications.map((notification) => (
                <div
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  key={notification.id}
                >
                  <div className="notification-icon">
                    <span className="material-icons">{getNotificationIcon(notification.type)}</span>
                  </div>
                  <div className="notification-content">
                    <div className="notification-header">
                      <h3 className="notification-title">{notification.title}</h3>
                      <div className="notification-date">
                        {notification.date.toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <div className="notification-message">{notification.message}</div>

                    {/* Logros individuales expandibles */}
                    {notification.type === 'achievement' && notification.metadata?.logros && notification.metadata.logros.length > 0 && (
                      <div className="notification-logros-section">
                        <button
                          className="btn btn-sm btn-link"
                          onClick={() => toggleExpandNotification(notification.id)}
                          style={{ padding: '4px 8px', marginTop: '8px' }}
                        >
                          {expandedNotifications.has(notification.id) ? '▲ Ocultar logros' : '▼ Ver logros individuales'}
                        </button>

                        {expandedNotifications.has(notification.id) && (
                          <div className="notification-logros-list" style={{ marginTop: '12px' }}>
                            {notification.metadata.logros.map((logro, index) => {
                              console.log('Logro data:', logro); // DEBUG
                              const futbolistaGenero = notification.metadata.futbolista_genero || 'M';
                              const normalizedLogroNombre = normalizeLogroNombre(logro.nombre);
                              const logroImageUrl = getLogroImageUrl(logro.nombre, futbolistaGenero);
                              const logroDisplayName = getLogroDisplayName(logro.nombre);

                              return (
                                <div key={index} className="notification-logro-item" style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '12px',
                                  padding: '16px',
                                  marginBottom: '12px',
                                  backgroundColor: '#ffffff',
                                  borderRadius: '12px',
                                  border: '2px solid #ffc107',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                  {/* Imagen de la carta - Grande y centrada */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginBottom: '8px'
                                  }}>
                                    <img
                                      src={logroImageUrl}
                                      alt={logroDisplayName}
                                      style={{
                                        width: '180px',
                                        height: '240px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                      }}
                                    />
                                  </div>

                                  {/* Información del logro */}
                                  <div style={{ textAlign: 'center' }}>
                                    <h5 style={{
                                      margin: '0 0 8px 0',
                                      fontSize: '18px',
                                      fontWeight: 'bold',
                                      color: '#212529'
                                    }}>
                                      🏆 {logroDisplayName}
                                    </h5>
                                    <p style={{
                                      margin: '0 0 12px 0',
                                      fontSize: '14px',
                                      color: '#6c757d',
                                      lineHeight: '1.5'
                                    }}>
                                      {logro.descripcion}
                                    </p>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '6px 16px',
                                      backgroundColor: '#ffc107',
                                      color: '#000',
                                      borderRadius: '20px',
                                      fontSize: '14px',
                                      fontWeight: 'bold',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      ⭐ {logro.puntos} ProPoints
                                    </span>
                                  </div>

                                  {/* Botones de compartir y descargar */}
                                  <div onClick={(e) => e.stopPropagation()} style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    marginTop: '8px'
                                  }}>
                                    {/* Botón de compartir */}
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                      <ShareSocialButton
                                        title={`🏆 ${logroDisplayName}`}
                                        text={`¡He conseguido el logro "${logroDisplayName}" en Liga ProKids!`}
                                        url={`${window.location.origin}/share/partido/${notification.metadata.partidoId}/futbolista/${notification.metadata.futbolistaId}/logro/${normalizedLogroNombre}`}
                                        hashtags={['LigaProKids', 'ProPoints', logroDisplayName.replace(/\s+/g, '')]}
                                        onShare={(platform) => {
                                          console.log(`Compartido logro ${logro.nombre} en ${platform}`);
                                          API.post('/postgres/share/track', {
                                            partidoId: notification.metadata.partidoId,
                                            futbolistaId: notification.metadata.futbolistaId,
                                            logroNombre: normalizedLogroNombre,
                                            platform
                                          }).catch(err => console.error('Error tracking share:', err));
                                        }}
                                      />
                                    </div>

                                    {/* Botón de descargar para Instagram */}
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                      <button
                                        onClick={() => generateImageForInstagram(
                                          {
                                            nombre: logro.nombre,
                                            descripcion: logro.descripcion,
                                            puntos: logro.puntos
                                          },
                                          {
                                            nombre: notification.metadata.futbolista_nombre || 'Futbolista',
                                            genero: notification.metadata.futbolista_genero || 'M',
                                            futbolistaId: notification.metadata.futbolistaId
                                          },
                                          {
                                            partidoId: notification.metadata.partidoId,
                                            jornada: notification.metadata.jornada_numero,
                                            sede: notification.metadata.sede_nombre,
                                            torneo: notification.metadata.torneo_nombre,
                                            fecha: notification.metadata.jornada_fecha
                                          }
                                        )}
                                        disabled={generatingImage === `${notification.metadata.partidoId}_${notification.metadata.futbolistaId}_${logro.nombre}`}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          background: generatingImage === `${notification.metadata.partidoId}_${notification.metadata.futbolistaId}_${logro.nombre}`
                                            ? '#ccc'
                                            : 'linear-gradient(135deg, #E1306C 0%, #C13584 50%, #833AB4 100%)',
                                          color: 'white',
                                          padding: '10px 20px',
                                          borderRadius: '12px',
                                          border: 'none',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          boxShadow: generatingImage === `${notification.metadata.partidoId}_${notification.metadata.futbolistaId}_${logro.nombre}`
                                            ? 'none'
                                            : '0 4px 12px rgba(225, 48, 108, 0.4)',
                                          cursor: generatingImage === `${notification.metadata.partidoId}_${notification.metadata.futbolistaId}_${logro.nombre}`
                                            ? 'not-allowed'
                                            : 'pointer',
                                          transition: 'transform 0.2s'
                                        }}
                                      >
                                        <span style={{ fontSize: '1.2rem' }}>📸</span>
                                        {generatingImage === `${notification.metadata.partidoId}_${notification.metadata.futbolistaId}_${logro.nombre}`
                                          ? 'Generando...'
                                          : 'Descargar para Instagram'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="notification-actions">
                      {notification.link && (
                        <Link to={notification.link} className="btn btn-sm">
                          Ver Detalles
                        </Link>
                      )}
                      {!notification.read && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          Marcar como leída
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteNotification(notification.id)}
                      >
                        Eliminar
                      </button>

                      {/* Botón de compartir TODOS los logros */}
                      {notification.type === 'achievement' && notification.metadata?.partidoId && notification.metadata?.futbolistaId && (
                        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '8px' }}>
                          <ShareSocialButton
                            title={`🏆 ${notification.title}`}
                            text={`${notification.message}`}
                            url={`${window.location.origin}/share/partido/${notification.metadata.partidoId}/futbolista/${notification.metadata.futbolistaId}`}
                            hashtags={['LigaProKids', 'ProPoints', 'Futbol']}
                            onShare={(platform) => {
                              console.log(`Compartido en ${platform}`, { notificationId: notification.id });
                              API.post('/postgres/share/track', {
                                partidoId: notification.metadata.partidoId,
                                futbolistaId: notification.metadata.futbolistaId,
                                platform
                              }).catch(err => console.error('Error tracking share:', err));
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
